import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { API_BASE_URI } from "../../http/api";
import { SearchGroup } from "@core/data/types/common";
import { Calculation, Characteristic, Index, Measurement, MeasurementSubjectGroup, Property } from "@library";
import { Filter, FilterBuilder } from "../../http/filter";
import { AnyReportTemplate, ReportAssetOption, ReportAssetPage, ReportImageOption, ReportImagePage } from "@core/data/types/advanced-report";
import { SizeClassification } from 'library/src/classifications/_types';

@Injectable()
export class AdvancedReportCompilerService {

    constructor(
        @Inject(API_BASE_URI) private _baseUri: string,
        private _http: HttpClient,
    ) {}

    // Paginated: the server snaps each page to the nearest evaluation boundary (never splits one
    // evaluation's photos across two pages) — see Culteva\Services\ImageCompiler::compileImages().
    images(reportKey: string, ownerOrgKey: string, page: number = 1) {
        return this._http.get<ReportImagePage>(`${this._baseUri}/report-compiler/images/${reportKey}/${ownerOrgKey}`, {
            params: { page: String(page) },
        });
    }

    // Org-wide shared image library ("Logos & Graphics", story 2.2/2.3). Paginated — see
    // Culteva\Services\ImageCompiler::listOrgAssets().
    orgAssets(orgKey: string, page: number = 1) {
        return this._http.get<ReportAssetPage>(`${this._baseUri}/organizations/${orgKey}/report-assets`, {
            params: { page: String(page) },
        });
    }

    uploadOrgAsset(orgKey: string, name: string, file: File) {
        let formData = new FormData();
        formData.append('name', name);
        formData.append('file', file);

        return this._http.post<ReportAssetOption>(`${this._baseUri}/organizations/${orgKey}/report-assets/upload`, formData);
    }

    // Report-scoped uploads ("Upload" tab, story 2.4) — invisible outside this report (server filters by
    // reportKey). Paginated — see Culteva\Services\ImageCompiler::listReportAssets().
    reportAssets(reportKey: string, page: number = 1) {
        return this._http.get<ReportAssetPage>(`${this._baseUri}/advanced-report/${reportKey}/assets`, {
            params: { page: String(page) },
        });
    }

    uploadReportAsset(reportKey: string, name: string, file: File) {
        let formData = new FormData();
        formData.append('name', name);
        formData.append('file', file);

        return this._http.post<ReportAssetOption>(`${this._baseUri}/advanced-report/${reportKey}/assets/upload`, formData);
    }

    // Covers both org-library and report-scoped assets — the server resolves ownership from the key.
    deleteAsset(key: string) {
        return this._http.delete<ReportAssetOption>(`${this._baseUri}/report-assets/${key}`);
    }

    // Covers both org-library and report-scoped assets — the server resolves ownership from the key.
    renameAsset(key: string, name: string) {
        return this._http.patch<ReportAssetOption>(`${this._baseUri}/report-assets/${key}`, { name });
    }

    // TODO REFACTOR TO ACCEPT SearchGroup
    chartOptions(reportKey: string, ownerOrgKey: string) {
        return this._http.get<{measures: SearchGroup<Measurement>[]}>(`${this._baseUri}/report-compiler/chart/options/${reportKey}/${ownerOrgKey}`, { });
    }

    templateOptions(reportKey: string, ownerOrgKey: string, filter: Filter) {
        let params = {
            ...FilterBuilder.queryParams(filter),
        }
        return this._http.get<{templates: SearchGroup<AnyReportTemplate>[]}>(`${this._baseUri}/report-compiler/template/options/${reportKey}/${ownerOrgKey}`, { params });
    }

    indexOptions(reportKey: string, ownerOrgKey: string) {
        return this._http.get<{
            characteristics: SearchGroup<Characteristic>[],
            indexes: SearchGroup<Index>[],
        }>(`${this._baseUri}/report-compiler/index/options/${reportKey}/${ownerOrgKey}`, { });
    }

    tableOptions(reportKey: string, ownerOrgKey: string) {
        return this._http.get<{
            calculations: SearchGroup<Calculation>[],
            characteristics: SearchGroup<Characteristic>[],
            indexes: SearchGroup<Index>[],
            measures: SearchGroup<Measurement>[],
            properties: SearchGroup<Property>[],
            classifications: SearchGroup<SizeClassification>[],
        }>(`${this._baseUri}/report-compiler/table/options/${reportKey}/${ownerOrgKey}`, { });
    }

    ganttOptions(reportKey: string, ownerOrgKey: string) {
        return this._http.get<{ characteristics: SearchGroup<Characteristic>[] }>(
            `${this._baseUri}/report-compiler/gantt/options/${reportKey}/${ownerOrgKey}`, {}
        );
    }

}
